import type { OctreeGeometryNode } from '@/plugins/pointCloud/core/OctreeGeometry'

/** 多视口帧末加载调度结果。 */
export interface PointCloudLoadScheduleResult {
  loadsStarted: number
  candidatesAttempted: number
}

interface SharedCandidate {
  node: OctreeGeometryNode
  viewCount: number
  firstRank: number
  insertionOrder: number
}

/** 在多个视口候选队列之间去重并跨帧公平启动点云节点加载。 */
export class PointCloudLoadScheduler {
  private nextViewId: string | null = null

  reset(): void {
    this.nextViewId = null
  }

  schedule(
    candidatesByView: ReadonlyMap<string, readonly OctreeGeometryNode[]>,
    visibleViewIds: readonly string[],
    maxLoads: number
  ): PointCloudLoadScheduleResult {
    const budget = Math.max(0, Math.floor(maxLoads))
    if (budget === 0) {
      return { loadsStarted: 0, candidatesAttempted: 0 }
    }

    const activeViewIds: string[] = []
    const queues = new Map<string, readonly OctreeGeometryNode[]>()
    const seenViewIds = new Set<string>()
    for (const viewId of visibleViewIds) {
      if (seenViewIds.has(viewId)) {
        continue
      }
      seenViewIds.add(viewId)
      const candidates = candidatesByView.get(viewId)
      if (!candidates || candidates.length === 0) {
        continue
      }
      activeViewIds.push(viewId)
      queues.set(viewId, candidates)
    }

    if (activeViewIds.length === 0) {
      return { loadsStarted: 0, candidatesAttempted: 0 }
    }

    let loadsStarted = 0
    let candidatesAttempted = 0
    const attemptedNodes = new Set<OctreeGeometryNode>()

    const sharedCandidates = this.collectSharedCandidates(activeViewIds, queues)
    for (const candidate of sharedCandidates) {
      if (loadsStarted >= budget) {
        break
      }
      attemptedNodes.add(candidate.node)
      candidatesAttempted++
      if (candidate.node.load()) {
        loadsStarted++
      }
    }

    const nextCandidateIndexByView = new Map(activeViewIds.map((viewId) => [viewId, 0]))
    let cursor = this.resolveStartIndex(activeViewIds)
    let consecutiveEmptyViews = 0
    while (loadsStarted < budget && consecutiveEmptyViews < activeViewIds.length) {
      const viewId = activeViewIds[cursor]
      cursor = (cursor + 1) % activeViewIds.length
      const candidate = this.takeNextCandidate(
        viewId,
        queues.get(viewId)!,
        nextCandidateIndexByView,
        attemptedNodes
      )
      if (!candidate) {
        consecutiveEmptyViews++
        continue
      }

      consecutiveEmptyViews = 0
      candidatesAttempted++
      if (candidate.load()) {
        loadsStarted++
        this.nextViewId = activeViewIds[cursor] ?? null
      }
    }

    return { loadsStarted, candidatesAttempted }
  }

  private collectSharedCandidates(
    activeViewIds: readonly string[],
    queues: ReadonlyMap<string, readonly OctreeGeometryNode[]>
  ): SharedCandidate[] {
    const memberships = new Map<OctreeGeometryNode, SharedCandidate>()
    let insertionOrder = 0

    for (const viewId of activeViewIds) {
      const seenNodes = new Set<OctreeGeometryNode>()
      const candidates = queues.get(viewId) ?? []
      for (let rank = 0; rank < candidates.length; rank++) {
        const node = candidates[rank]
        if (seenNodes.has(node)) {
          continue
        }
        seenNodes.add(node)
        const existing = memberships.get(node)
        if (existing) {
          existing.viewCount++
          existing.firstRank = Math.min(existing.firstRank, rank)
        } else {
          memberships.set(node, {
            node,
            viewCount: 1,
            firstRank: rank,
            insertionOrder: insertionOrder++,
          })
        }
      }
    }

    return Array.from(memberships.values())
      .filter((candidate) => candidate.viewCount > 1)
      .sort((left, right) => {
        if (left.viewCount !== right.viewCount) {
          return right.viewCount - left.viewCount
        }
        if (left.firstRank !== right.firstRank) {
          return left.firstRank - right.firstRank
        }
        return left.insertionOrder - right.insertionOrder
      })
  }

  private resolveStartIndex(activeViewIds: readonly string[]): number {
    if (!this.nextViewId) {
      return 0
    }
    const index = activeViewIds.indexOf(this.nextViewId)
    return index >= 0 ? index : 0
  }

  private takeNextCandidate(
    viewId: string,
    candidates: readonly OctreeGeometryNode[],
    nextCandidateIndexByView: Map<string, number>,
    attemptedNodes: Set<OctreeGeometryNode>
  ): OctreeGeometryNode | null {
    let index = nextCandidateIndexByView.get(viewId) ?? 0
    while (index < candidates.length) {
      const candidate = candidates[index]
      index++
      nextCandidateIndexByView.set(viewId, index)
      if (attemptedNodes.has(candidate)) {
        continue
      }
      attemptedNodes.add(candidate)
      return candidate
    }
    return null
  }
}
